/**
 * @fileoverview Generic dashboard quick access grid with fixed-width buttons
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { APP_COLORS } from "../../theme/appTheme";

/**
 * QuickAccessButton - Individual button with fixed width
 */
const QuickAccessButton = ({ label, icon: Icon, route, color, onClick }) => {
    const navigate = useNavigate();
    const buttonColor = color || APP_COLORS.primary.main;

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else if (route) {
            navigate(route);
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                width: { xs: '100%', sm: 110 },
                minWidth: { xs: 'unset', sm: 110 },
                maxWidth: { xs: 150, sm: 'none' },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${buttonColor}15`,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: APP_COLORS.elevation?.md || '0 4px 12px rgba(0,0,0,0.15)',
                        borderColor: buttonColor,
                        backgroundColor: `${buttonColor}25`,
                    }
                }}
            >
                {Icon && <Icon sx={{ fontSize: 36, color: buttonColor }} />}
            </Paper>
            <Typography
                variant="caption"
                sx={{
                    mt: 1,
                    fontWeight: 500,
                    color: 'text.primary',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    wordBreak: 'break-word',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

QuickAccessButton.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    route: PropTypes.string,
    color: PropTypes.string,
    onClick: PropTypes.func
};

/**
 * DashboardQuickAccessGrid - A grid of quick access buttons with consistent spacing
 */
export const DashboardQuickAccessGrid = ({ buttons = [] }) => {
    if (!buttons || buttons.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(4, 110px)',
                    md: 'repeat(auto-fill, 110px)'
                },
                gap: 3,
                justifyContent: { xs: 'stretch', sm: 'flex-start' }
            }}
        >
            {buttons.map((button, index) => (
                <QuickAccessButton
                    key={button.key || index}
                    label={button.label}
                    icon={button.icon}
                    route={button.route}
                    color={button.color}
                    onClick={button.onClick}
                />
            ))}
        </Box>
    );
};

DashboardQuickAccessGrid.propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string.isRequired,
        icon: PropTypes.elementType,
        route: PropTypes.string,
        color: PropTypes.string,
        onClick: PropTypes.func
    }))
};
